/**
 * Report clipboard serialization — the single source of truth for how a
 * report leaves this app.
 *
 * HISTORY (do not re-learn this the hard way):
 * The radiologist pastes into Karisma (Kestral RIS). Karisma's paste parser:
 *  - chokes on rich clipboard flavors from constructed HTML ("Unable to peek
 *    0 characters") — so the Karisma path must be PLAIN TEXT ONLY;
 *  - is line-based and Windows-native — line endings must be CRLF (\r\n).
 *    Browser Blob/writeText payloads keep whatever we put in them; LF-only
 *    text is what previous attempts shipped, and Karisma rejected it;
 *  - section headings are TIGHT (no blank line after them) — but the exam
 *    title (h1/h2 study name) gets ONE blank line after it (radiologist
 *    request 2026-06-12; supersedes the earlier all-headings-tight rule);
 *  - the radiologist wants AN EMPTY LINE BETWEEN PARAGRAPHS ("double
 *    spacing") — paragraph→paragraph and paragraph→heading get one blank
 *    line.
 * Word, by contrast, wants the HTML flavor with inline line-height — that is
 * the separate "rich" payload, never mixed into the Karisma path.
 *
 * Used by BOTH copy paths:
 *  - the Copy button (ExportPanel)
 *  - native Ctrl+C from the TipTap editor (ReportEditor's
 *    clipboardTextSerializer) — the radiologist's actual day-to-day path.
 */

const HEADINGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

/** Extract text from an element, converting <br> to \n. */
function extractText(el: Element): string {
  let result = '';
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      result += child.textContent || '';
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const childEl = child as Element;
      if (childEl.tagName === 'BR') {
        result += '\n';
      } else {
        result += extractText(childEl);
      }
    }
  }
  return result;
}

interface Block {
  isHeading: boolean;
  isTitle: boolean; // h1/h2 exam title — gets a blank line AFTER it
  text: string;
}

/** Join logical blocks using the Karisma/radiologist spacing rules.
 *  join(newline) already puts ONE newline between elements, so:
 *  section heading → anything = push nothing extra (tight);
 *  exam title (h1/h2) → anything = one blank line;
 *  paragraph → anything = one blank line. */
function joinBlocks(blocks: Block[], newline: string): string {
  const out: string[] = [];
  let prevWasTightHeading = false;
  blocks.forEach((b, i) => {
    if (i > 0 && !prevWasTightHeading) {
      out.push(''); // one blank line between blocks
    }
    out.push(b.text.replace(/\n/g, newline));
    prevWasTightHeading = b.isHeading && !b.isTitle;
  });
  // Collapse accidental runs of blank lines, trim edges.
  return out
    .join(newline)
    .replace(new RegExp(`(?:${newline}){3,}`, 'g'), `${newline}${newline}`)
    .trim();
}

/** Parse report HTML into logical blocks (headings, paragraphs, list items). */
function htmlToBlocks(html: string): Block[] {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const blocks: Block[] = [];
  for (const node of Array.from(tempDiv.children)) {
    const tag = node.tagName;
    if (tag === 'UL' || tag === 'OL') {
      node.querySelectorAll('li').forEach((li) => {
        const liText = extractText(li).trim();
        if (liText) blocks.push({ isHeading: false, isTitle: false, text: liText });
      });
      continue;
    }
    const text = extractText(node).trim();
    if (!text) continue;
    blocks.push({
      isHeading: HEADINGS.has(tag),
      isTitle: tag === 'H1' || tag === 'H2',
      text,
    });
  }
  return blocks;
}

/**
 * Report HTML → plain text for the clipboard.
 * CRLF by default — Windows apps (Karisma, Notepad, Word paste-as-text)
 * are line-based on \r\n.
 */
export function htmlToPlainText(html: string, newline = '\r\n'): string {
  return joinBlocks(htmlToBlocks(html), newline);
}

/**
 * Insert REAL empty paragraphs so the report reads with the same blank-line
 * spacing whether it's viewed on screen or pasted as HTML into Karisma.
 *
 * Karisma pastes the HTML clipboard flavor (bold survives), and in HTML only a
 * real empty paragraph renders as a blank line — a CSS margin is visual only
 * and copies as nothing. So we materialise every gap as an empty <p>, exactly
 * like the radiologist pressing Enter by hand.
 *
 * Mirrors the plain-text spacing rules (joinBlocks): a blank line before every
 * block EXCEPT the first, and EXCEPT content directly under a section heading
 * (those stay tight). Net effect: blank line between body paragraphs, blank
 * line before each section heading and before the first section after the exam
 * title, tight under each heading.
 *
 * Idempotent: never stacks blanks and never adds a leading one.
 */
export function withSectionSpacing(html: string): string {
  if (!html) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  const isEmptyP = (el: Element | null) =>
    !!el && el.tagName === 'P' && !(el.textContent || '').trim();
  const isSectionHeading = (el: Element | null) => !!el && el.tagName === 'H3';

  Array.from(container.children).forEach((node, i) => {
    if (i === 0) return;             // no leading blank
    if (isEmptyP(node)) return;      // don't process / stack existing blanks
    const prev = node.previousElementSibling;
    if (isEmptyP(prev)) return;      // idempotent: a blank is already here
    if (isSectionHeading(prev)) return; // section heading -> content stays tight
    container.insertBefore(document.createElement('p'), node);
  });
  return container.innerHTML;
}

/**
 * ProseMirror clipboard slice → plain text, same spacing rules, for native
 * Ctrl+C from the editor. Typed loosely so this module doesn't depend on
 * prosemirror packages directly.
 */
export function sliceToPlainText(slice: {
  content: { forEach: (fn: (node: any) => void) => void };
}): string {
  const blocks: Block[] = [];

  const pushNode = (node: any) => {
    const type: string = node.type?.name || '';
    if (type === 'orderedList' || type === 'bulletList') {
      node.forEach((li: any) => {
        const text = (li.textContent || '').trim();
        if (text) blocks.push({ isHeading: false, isTitle: false, text });
      });
      return;
    }
    // hardBreak-aware text: walk children so <br> inside a paragraph
    // becomes \n rather than disappearing.
    let text = '';
    if (node.isTextblock) {
      node.forEach((child: any) => {
        if (child.type?.name === 'hardBreak') text += '\n';
        else text += child.text || child.textContent || '';
      });
    } else {
      text = node.textContent || '';
    }
    text = text.trim();
    if (!text) return;
    blocks.push({
      isHeading: type === 'heading',
      isTitle: type === 'heading' && (node.attrs?.level ?? 2) <= 2,
      text,
    });
  };

  slice.content.forEach(pushNode);
  return joinBlocks(blocks, '\r\n');
}

/**
 * Report HTML → clean rich HTML for pasting into Word.
 * Inline line-height 200% (Word maps it to "Multiple 2" spacing) + explicit
 * margins. Built from scratch — never TipTap's raw markup.
 */
export function buildClipboardHtml(html: string): string {
  const S = 'style="color:#000;margin:0 0 6pt 0;font-weight:normal;line-height:200%;"';
  const SB = 'style="color:#000;margin:12pt 0 6pt 0;font-weight:bold;line-height:200%;"';
  // Exam title: extra space after (the radiologist's blank-line-after-title request).
  const SB_FIRST = 'style="color:#000;margin:0 0 16pt 0;font-weight:bold;line-height:200%;"';

  const parts: string[] = [];
  let isFirstHeading = true;
  for (const b of htmlToBlocks(html)) {
    if (b.isHeading) {
      parts.push(`<p ${isFirstHeading ? SB_FIRST : SB}>${escapeHtml(b.text)}</p>`);
      isFirstHeading = false;
    } else {
      parts.push(`<p ${S}>${escapeHtml(b.text).replace(/\n/g, '<br>')}</p>`);
    }
  }

  return [
    '<html><head><meta charset="utf-8"></head>',
    '<body style="color:#000;font-family:Calibri,Arial,sans-serif;font-size:11pt;">',
    parts.join(''),
    '</body></html>',
  ].join('');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Write plain text to the clipboard with maximum compatibility:
 * async Clipboard API in secure contexts, hidden-textarea execCommand
 * fallback elsewhere (e.g. the app accessed over LAN http://).
 * MUST be called synchronously inside the user gesture — never after an
 * await (Chromium's transient user activation expires; Safari's immediately).
 */
export async function copyPlainText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  legacyExecCopy(() => {
    const ta = document.createElement('textarea');
    ta.value = text;
    return ta;
  });
}

/**
 * Write rich HTML (+ plain-text fallback flavor) to the clipboard.
 * ClipboardItem where supported; contenteditable execCommand copy fallback
 * (which natively yields both text/html and text/plain flavors).
 */
export async function copyRichHtml(htmlPayload: string, textPayload: string): Promise<void> {
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([htmlPayload], { type: 'text/html' }),
        'text/plain': new Blob([textPayload], { type: 'text/plain' }),
      }),
    ]);
    return;
  }
  legacyExecCopy(() => {
    const div = document.createElement('div');
    div.contentEditable = 'true';
    div.innerHTML = htmlPayload;
    return div;
  });
}

function legacyExecCopy(makeEl: () => HTMLElement): void {
  const el = makeEl();
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  el.style.top = '0';
  document.body.appendChild(el);
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    if (el instanceof HTMLTextAreaElement) {
      el.select();
    }
    const ok = document.execCommand('copy');
    sel?.removeAllRanges();
    if (!ok) throw new Error('execCommand copy returned false');
  } finally {
    document.body.removeChild(el);
  }
}
